import h from '../global/create-element.js';

const toastContainerClass = 'ToastContainer';
const toastClass = 'Toast';
const toastIconClass = 'Toast__Icon';
const toastMessageClass = 'Toast__Message';
const toastDismissClass = 'Toast__Dismiss';

const autoHideCache = {};

const DEFAULT_SHOW_TOAST_OPTIONS = {
  root: document.body,
  iconUrl: null,
  autoHide: false,
  autoHideDelay: 3000,
  allowDismiss: true,
};
const DEFAULT_HIDE_TOAST_OPTIONS = {
  root: document.body,
};

/**
 * @param {String} message The message to display inside the toast component.
 * @param {Object} options
 * @param {String} options.id A unique identifier for this toast message, to be used later to reference this instance.
 *  If not provided, a random value will be generated.
 * @param {HTMLElement} options.root The element where the toasts are going to be rendered to.
 * @param {String} options.iconUrl The URL to the icon image to show in the toast. No icon is displayed if not provided.
 * @param {Boolean} options.autoHide Whether the toast is automatically hidden after a certain time or not.
 * @param {Number} options.autoHideDelay The delay in milliseconds before automatically hiding the toast. Requires
 *  autoHide=true, otherwise ignored.
 * @param {Boolean} options.allowDismiss Whether the toast can be manually dismissed with a close button or not.
 *
 * @return {String} The unique identifier for this toast instance.
 */
export function showToast(message, options={}) {
  options = Object.assign({}, DEFAULT_SHOW_TOAST_OPTIONS, options);
  options.id = options.id || getRandomId();
  const { id, iconUrl, autoHide, allowDismiss } = options;

  const $icon = iconUrl && h('img', { 'class': toastIconClass, src: iconUrl });
  const $message = h('p', { 'class': toastMessageClass }, message);
  const $dismiss = allowDismiss && h('button', {
    'class': toastDismissClass,
    onclick: () => hideToast(id, options)
  });

  const $toast = h(
    'div',
    { 'class': toastClass, 'data-id': id },
    [$icon, $message, $dismiss].filter(Boolean)
  );

  hideToast(id, options);

  const $container = getOrCreateToastContainer(options);
  $container.append($toast);

  if (autoHide) {
    startAutoHide($toast, options);
  }

  return id;
}

/**
 * @param {String} id The unique identifier of the toast instance to hide.
 * @param {Object} options
 * @param {HTMLElement} options.root The element where the toasts are going to be rendered to.
 *
 * @returns {undefined}
 */
export function hideToast(id, options={}) {
  const { root } = Object.assign({}, DEFAULT_HIDE_TOAST_OPTIONS, options);

  const $container = root.querySelector(`:scope > .${toastContainerClass}`);
  if (!$container) {
    return;
  }

  const $toast = $container.querySelector(`[data-id="${id}"]`);
  if (!$toast) {
    return;
  }

  $toast.remove();
  endAutoHide(id);

  if ($container.childElementCount === 0) {
    $container.remove();
  }
}

function getOrCreateToastContainer({ root }) {
  let $container = root.querySelector(`:scope > .${toastContainerClass}`);
  if (!$container) {
    $container = h('div', { 'class': toastContainerClass });
    root.append($container);
  }
  return $container;
}

function getRandomId() {
  return String(Math.random()).substring(2);
}

function startAutoHide($toast, options) {
  const { id, autoHideDelay } = options;
  autoHideCache[id] = setTimeout(() => hideToast(id, options),  autoHideDelay);
}

function endAutoHide(id) {
  if (autoHideCache.hasOwnProperty(id)) {
    clearTimeout(autoHideCache[id]);
    delete autoHideCache[id];
  }
}
